import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";

export default function IntervieweeChat(){
  const { sessionId } = useParams();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [completed, setCompleted] = useState(false);
  const [intervieweeName, setIntervieweeName] = useState("");
  const [interviewerName, setInterviewerName] = useState("Interviewer");
  const bottomRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [recording, setRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    const el = document.querySelector(".input");
    if (el) {
      el.style.height = "auto";
      const newHeight = Math.min(el.scrollHeight, 224); // 1.4em * 6 ≈ 134px
      el.style.height = newHeight + "px";
    }
  }, [input]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}`)
      .then(async res => {
        const session = res.data;
        setMessages(session.transcript || []);
        setCompleted(session.completed || false);
        setIntervieweeName(session.interviewee_name);

        // fetch interviewer name using interview_id
        try {
          const interviewRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/interviewers/${session.interview_id}`);
          setInterviewerName(interviewRes.data.name || "Interviewer");
        } catch {
          setInterviewerName("Interviewer");
        }
      })
      .catch(()=>{});
  }, [sessionId]);

  useEffect(()=>{ bottomRef.current?.scrollIntoView({behavior:"smooth"}) }, [messages, completed]);

  const sendMessage = async () => {
    if (!input.trim() || completed) return;
    const userMsg = { role: "user", content: input };
    setMessages(prev => [...prev, userMsg]);
    setInput("");
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}/chat`, { message: input });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.reply }]);
      setProgress(res.data.progress);
      if (res.data.completed) setCompleted(true);
    } catch {
      alert("Error sending message");
    }
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorderRef.current = new MediaRecorder(stream);
    audioChunksRef.current = [];

    mediaRecorderRef.current.ondataavailable = e => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    mediaRecorderRef.current.onstop = async () => {
      const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");

      // send to backend for transcription
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sessions/${sessionId}/transcribe`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setInput(res.data.text); // put transcribed text into input box
    };

    mediaRecorderRef.current.start();
    setRecording(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  // text-to-speech
  const speak = (text) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "en-US";
    window.speechSynthesis.speak(utterance);
  };

  return (
    <div className="container-chat">
      <h2>Interview</h2>
      <div className="card">
        {!completed && (
          <div style={{marginBottom:10}}>
            <div style={{fontSize:12}}>Interview progress: {progress}%</div>
            <div style={{height:"8px",background:"#eee",borderRadius:"4px",overflow:"hidden"}}>
              <div style={{width:`${progress}%`,background:"var(--accent)",height:"100%"}} />
            </div>
          </div>
        )}

        <div className="chat-window" style={{display:"flex",flexDirection:"column"}}>
          {messages.map((m,i)=>(
            <div key={i} className={`chat-row ${m.role === "user" ? "user" : "assistant"}`}>
              <div className={`bubble ${m.role === "user" ? "user" : "assistant"}`}>
                <div style={{fontSize:13,color:"var(--muted)",marginBottom:6}}>
                  {m.role === "user" ? intervieweeName : interviewerName}
                </div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span>{m.content}</span>
                  {m.role === "assistant" && (
                    <button 
                      onClick={() => speak(m.content)} 
                      style={{
                        background:"none",
                        border:"none",
                        cursor:"pointer",
                        color:"var(--accent)",
                        fontSize:"16px"
                      }}
                      title="Read aloud"
                    >
                      🔊
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>

        {!completed ? (
          <div style={{position:"relative",flex:1,marginTop:12,display:"flex",alignItems:"center"}}>
            <textarea
              className="input"
              value={input}
              onChange={e=>setInput(e.target.value)}
              placeholder="Type your answer..."
              rows={1}
              style={{
                flex:1,
                paddingRight:"40px",
                lineHeight:"1.4em",
                resize:"none",
                overflowY:"auto",
                maxHeight:"14em"
              }}
            />
            <button 
              className="btn" 
              onClick={sendMessage}
              style={{
                position:"absolute",
                right:"50px",
                bottom:"8px",
                width:"28px",
                height:"28px",
                borderRadius:"50%",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                padding:0
              }}
              title="Send"
            >
              ➤
            </button>
            <button 
              className={`btn ${recording ? "danger" : "secondary"}`}
              onClick={recording ? stopRecording : startRecording}
              style={{
                marginLeft:"8px",
                width:"36px",
                height:"36px",
                borderRadius:"50%",
                display:"flex",
                alignItems:"center",
                justifyContent:"center",
                padding:0,
                background: recording ? "var(--accent)" : "var(--muted)",
                color: "#fff",
                transition: "background 0.3s ease"
              }}
              title={recording ? "Stop Recording" : "Record Answer"}
            >
              {recording ? "⏹" : "🎤"}
            </button>

          </div>
        ) : (
          <div style={{marginTop:12}} className="center">
            <div className="badge success">Interview finished</div>
          </div>
        )}
      </div>
    </div>
  );
}
